/**
 * `?mock=1` — boot the webapp in a regular browser without Telegram.
 *
 * Installs:
 *   1. A stub `window.Telegram.WebApp` so `tg.*` getters return data.
 *   2. A `fetch` interceptor that answers `/api/v1/*` with fixtures.
 *
 * Anything not explicitly handled falls through to a permissive empty
 * response (`{ items: [], total: 0 }` for collections, `{}` otherwise)
 * so pages can render without crashing while we add proper fixtures
 * per phase. The flag persists in `localStorage.polybot.mock` so a
 * reload keeps the mode without re-typing the query param.
 */

const MOCK_TOKEN =
  'mock-eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.MOCK.MOCK';

const MOCK_USER = {
  id: '00000000-0000-0000-0000-000000000001',
  tg_id: 100200300,
  tg_username: 'mock_kit_pod_user',
  tg_first_name: 'Kit',
  tg_last_name: 'Pod',
  language: 'en',
  ref_code: 'KITPOD42',
  wallet_address: '0xC0FFEE0000000000000000000000000000000001',
  is_admin: false,
  platform_fee_pct: '20',
  referral_l1_pct: '10',
  referral_l2_pct: '2',
  bot_enabled: true,
  bots: {
    pm: { enabled: true, available: true, admin_paused: false },
    hl: { enabled: true, available: true, admin_paused: false },
  },
  kill_switches: {
    trading_paused: false,
    pm_trading_paused: false,
    hl_trading_paused: false,
    deposits_paused: false,
    withdrawals_paused: false,
    is_banned: false,
  },
  ui_theme: 'system',
} as const;

function mockEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('mock') === '1') {
    try {
      window.localStorage.setItem('polybot.mock', '1');
    } catch {
      /* private mode — fine, just won't persist */
    }
    return true;
  }
  if (params.get('mock') === '0') {
    try {
      window.localStorage.removeItem('polybot.mock');
    } catch {
      /* ignore */
    }
    return false;
  }
  try {
    return window.localStorage.getItem('polybot.mock') === '1';
  } catch {
    return false;
  }
}

function installMockTelegramWebApp() {
  if (typeof window === 'undefined') return;
  if (window.Telegram?.WebApp?.initData) return; // real WebApp already loaded
  const noop = () => {};
  const stub = {
    initData: 'mock=1',
    initDataUnsafe: {
      user: {
        id: MOCK_USER.tg_id,
        first_name: MOCK_USER.tg_first_name,
        last_name: MOCK_USER.tg_last_name,
        username: MOCK_USER.tg_username,
        language_code: MOCK_USER.language,
      },
    },
    version: '7.0',
    platform: 'web',
    colorScheme:
      window.matchMedia?.('(prefers-color-scheme: light)').matches
        ? 'light'
        : 'dark',
    themeParams: {},
    ready: noop,
    expand: noop,
    close: noop,
    openLink: (u: string) => window.open(u, '_blank'),
    openTelegramLink: (u: string) => window.open(u, '_blank'),
    HapticFeedback: {
      impactOccurred: noop,
      notificationOccurred: noop,
      selectionChanged: noop,
    },
    BackButton: { show: noop, hide: noop, onClick: noop, offClick: noop },
    MainButton: {
      setText: function () {
        return this;
      },
      show: noop,
      hide: noop,
      enable: noop,
      disable: noop,
      onClick: noop,
      offClick: noop,
    },
  };
  // Cast to Window['Telegram'] — the stub matches the runtime contract
  // closely enough; the type narrowing cost isn't worth a fuller mock.
  (window as unknown as { Telegram: { WebApp: unknown } }).Telegram = {
    WebApp: stub,
  };
}

type FixtureHandler = (
  body: unknown,
  url: URL,
) => unknown | Promise<unknown>;

const FIXTURES: Record<string, FixtureHandler> = {
  'POST /v1/auth/telegram': () => ({
    access_token: MOCK_TOKEN,
    token_type: 'bearer',
    expires_in: 3600,
    is_new: false,
    user: MOCK_USER,
  }),
  'POST /v1/auth/refresh': () => ({
    access_token: MOCK_TOKEN,
    token_type: 'bearer',
    expires_in: 3600,
  }),
  'POST /v1/auth/logout': () => ({}),
  'GET /v1/me': () => MOCK_USER,
  'PATCH /v1/me/preferences': (body) => ({
    ui_theme:
      (body as { ui_theme?: string } | undefined)?.ui_theme ?? MOCK_USER.ui_theme,
  }),
  'PATCH /v1/me/bot': (body) => {
    const patch = (body ?? {}) as { pm?: boolean; hl?: boolean };
    // Persist into MOCK_USER so the subsequent /v1/me refetch reflects
    // the flip (otherwise the toggle would snap back in preview).
    if (typeof patch.pm === 'boolean')
      (MOCK_USER.bots.pm as { enabled: boolean }).enabled = patch.pm;
    if (typeof patch.hl === 'boolean')
      (MOCK_USER.bots.hl as { enabled: boolean }).enabled = patch.hl;
    return { pm: { ...MOCK_USER.bots.pm }, hl: { ...MOCK_USER.bots.hl } };
  },
  'GET /v1/me/notification-prefs': () => ({
    prefs: {
      'deposit.confirmed': true,
      withdrawal_decision: true,
      fee_charged: true,
      'referral.earning': true,
      kill_switch: true,
      // trade.opened defaults off — high-frequency, opt-in.
      'trade.opened': false,
      'trade.settled': true,
      'trade.cancelled': true,
    },
  }),
  'PATCH /v1/me/notification-prefs': (body) => {
    const partial =
      ((body as { prefs?: Record<string, boolean> } | undefined)?.prefs) ?? {};
    const base = {
      'deposit.confirmed': true,
      withdrawal_decision: true,
      fee_charged: true,
      'referral.earning': true,
      kill_switch: true,
      'trade.opened': false,
      'trade.settled': true,
      'trade.cancelled': true,
    };
    return { prefs: { ...base, ...partial } };
  },
  'GET /v1/platform-config': () => ({
    withdrawal_fixed_fee_usd: '1.00',
    min_deposit_usd: '50.00',
    trading_paused: false,
    pm_trading_paused: false,
    hl_trading_paused: false,
    deposits_paused: false,
    // On in mock so the pUSD allocate/withdraw affordances are visible
    // in dev; prod default is off until the worker variants ship.
    withdrawals_paused: false,
    pusd_transfer_enabled: true,
    // On in mock so the live-quote allocate max + swap-all MAX are
    // exercised in dev; prod default is off until money-flow wires the
    // swap-all step handlers.
    transfer_live_quote_enabled: true,
  }),
  'GET /v1/transfers/quote': () => ({
    // Live-quote main→PM max: near-full balance, minus a token haircut
    // (mirrors the real endpoint's ~0.3% tolerance vs the old 1% buffer).
    max_transferable_usdc: '1999.40',
    canonical_covered_usdc: '0',
    needs_swap: true,
  }),
  'GET /v1/balance': () => ({
    items: [{ asset: 'USDC', available: '1234.56', locked: '120.00' }],
    total_usd: '1354.56',
  }),
  'GET /v1/balance/hl': () => ({
    withdrawable: '512.40',
    account_value: '548.10',
    positions: [
      {
        coin: 'BTC',
        size: '0.05',
        side: 'LONG',
        entry_price: '60000',
        mark_price: '60500',
        position_value: '3025',
        unrealized_pnl: '25',
      },
    ],
  }),
  'GET /v1/deposits/address': (_body, url) => {
    const service = url?.searchParams.get('service');
    const polygon = {
      chain_id: 137,
      name: 'Polygon',
      short_name: 'polygon',
      native_symbol: 'MATIC',
      confirmations_required: 12,
      explorer_tx_url: 'https://polygonscan.com/tx/',
      tokens: [
        { symbol: 'USDC', address: '0x...', decimals: 6 },
        { symbol: 'USDT', address: '0x...', decimals: 6 },
      ],
    };
    const arbitrum = {
      chain_id: 42161,
      name: 'Arbitrum',
      short_name: 'arbitrum',
      native_symbol: 'ETH',
      confirmations_required: 20,
      explorer_tx_url: 'https://arbiscan.io/tx/',
      tokens: [{ symbol: 'USDC', address: '0x...', decimals: 6 }],
    };
    const supported =
      service === 'pm'
        ? [polygon]
        : service === 'hl'
          ? [arbitrum]
          : [polygon, arbitrum];
    return {
      address: '0xC0FFEE0000000000000000000000000000000001',
      supported_chains: supported,
    };
  },
  'POST /v1/deposits/intent': (body) => {
    const req = body as { chain_id: number; pm_pct: number; hl_pct: number };
    return {
      id: `dep-intent-mock-${Date.now()}`,
      chain_id: req.chain_id,
      allocation_intent: { pm_pct: req.pm_pct, hl_pct: req.hl_pct },
      status: 'seen',
      created_at: new Date().toISOString(),
    };
  },
  'GET /v1/internal-transfers': () => ({
    items: [
      {
        id: 'itx-mock-1',
        direction: 'pm_to_hl',
        amount_usdc: '100.00',
        status: 'completed',
        current_step: 'completed',
        failure_reason: null,
        requested_at: new Date(Date.now() - 600_000).toISOString(),
        completed_at: new Date(Date.now() - 540_000).toISOString(),
      },
      {
        id: 'itx-mock-2',
        direction: 'hl_to_pm',
        amount_usdc: '50.00',
        status: 'hl_withdrawing',
        current_step: 'hl_withdrawing',
        failure_reason: null,
        requested_at: new Date(Date.now() - 60_000).toISOString(),
        completed_at: null,
      },
    ],
    total: 2,
  }),
  // ─── Architecture C balances + transfers ──────────────────────
  'GET /v1/me/balances': () => ({
    main_wallet: {
      address: '0xC0FFEE0000000000000000000000000000000001',
      polygon: {
        chain_id: 137,
        short_name: 'polygon',
        name: 'Polygon',
        native_symbol: 'POL',
        tokens: [
          { symbol: 'POL', address: null, amount: '0.42' },
          {
            symbol: 'USDC',
            address: '0x3c499c542cef5e3811e1192ce70d8cc03d5c3359',
            amount: '320.00',
          },
          {
            symbol: 'USDT',
            address: '0xc2132d05d31c914a87c6611c10748aeb04b58e8f',
            amount: '50.00',
          },
          {
            symbol: 'USDC.e',
            address: '0x2791bca1f2de4661ed88a30c99a7a9449aa84174',
            amount: '12.34',
          },
          {
            // Non-zero so the EOA-pUSD allocate/withdraw affordances
            // render in dev mock (referral payouts land here).
            symbol: 'pUSD',
            address: '0xc011a7e12a19f7b1f670d46f03b03f3342e82dfb',
            amount: '15.50',
          },
        ],
        error: null,
      },
      arbitrum: {
        chain_id: 42161,
        short_name: 'arbitrum',
        name: 'Arbitrum One',
        native_symbol: 'ETH',
        tokens: [
          { symbol: 'ETH', address: null, amount: '0.0042' },
          {
            symbol: 'USDC',
            address: '0xaf88d065e77c8cc2239327c5edb3a432268e5831',
            amount: '180.00',
          },
          {
            symbol: 'USDT',
            address: '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9',
            amount: '0.00',
          },
          {
            symbol: 'USDC.e',
            address: '0xff970a61a04b1ca14834a43f5de4533ebddb5cc8',
            amount: '0.00',
          },
        ],
        error: null,
      },
    },
    pm_bot_active: {
      amount: '420.50',
      asset: 'pUSD',
      address: '0xDEFCAFE000000000000000000000000000000007',
      deployed: true,
      error: null,
    },
    hl_bot_active: {
      amount: '548.10',
      asset: 'USDC',
      address: '0xC0FFEE0000000000000000000000000000000001',
      deployed: true,
      error: null,
    },
    in_transit: [
      {
        id: 'tx-mock-in-flight-1',
        direction: 'eoa_to_pm',
        amount_usdc: '75.00',
        status: 'wrap_via_relay',
        current_step: 'wrap_via_relay',
        requested_at: new Date(Date.now() - 30_000).toISOString(),
      },
    ],
  }),

  'GET /v1/transfers': () => ({
    items: [
      {
        id: 'tx-mock-1',
        direction: 'eoa_to_pm',
        amount_usdc: '100.00',
        status: 'completed',
        current_step: null,
        failure_reason: null,
        requested_at: new Date(Date.now() - 3_600_000).toISOString(),
        completed_at: new Date(Date.now() - 3_500_000).toISOString(),
        data: { source_token: 'USDC.e', source_chain: 'polygon' },
      },
      {
        id: 'tx-mock-in-flight-1',
        direction: 'eoa_to_pm',
        amount_usdc: '75.00',
        status: 'wrap_via_relay',
        current_step: 'wrap_via_relay',
        failure_reason: null,
        requested_at: new Date(Date.now() - 30_000).toISOString(),
        completed_at: null,
        data: { source_token: 'USDC.e', source_chain: 'polygon' },
      },
      {
        id: 'tx-mock-2',
        direction: 'hl_to_eoa',
        amount_usdc: '40.00',
        status: 'failed_at_hl_withdrawing',
        current_step: null,
        failure_reason: 'HL info: insufficient withdrawable',
        requested_at: new Date(Date.now() - 86_400_000).toISOString(),
        completed_at: null,
        data: null,
      },
    ],
    total: 3,
  }),

  'POST /v1/transfers/allocate-pm': (body) => {
    const req = body as { source_token: string; source_chain: string; amount: string };
    return {
      id: `tx-mock-${Date.now()}`,
      direction: 'eoa_to_pm',
      amount_usdc: req.amount,
      status: 'pending',
      current_step: 'pending',
      failure_reason: null,
      requested_at: new Date().toISOString(),
      completed_at: null,
      data: { source_token: req.source_token, source_chain: req.source_chain },
    };
  },

  'POST /v1/transfers/allocate-hl': (body) => {
    const req = body as { source_token: string; source_chain: string; amount: string };
    return {
      id: `tx-mock-${Date.now()}`,
      direction: 'eoa_to_hl',
      amount_usdc: req.amount,
      status: 'pending',
      current_step: 'pending',
      failure_reason: null,
      requested_at: new Date().toISOString(),
      completed_at: null,
      data: { source_token: req.source_token, source_chain: req.source_chain },
    };
  },

  'POST /v1/transfers/deallocate-pm': (body) => ({
    id: `tx-mock-${Date.now()}`,
    direction: 'pm_to_eoa',
    amount_usdc: (body as { amount: string }).amount,
    status: 'pending',
    current_step: 'pending',
    failure_reason: null,
    requested_at: new Date().toISOString(),
    completed_at: null,
    data: null,
  }),

  'POST /v1/transfers/deallocate-hl': (body) => ({
    id: `tx-mock-${Date.now()}`,
    direction: 'hl_to_eoa',
    amount_usdc: (body as { amount: string }).amount,
    status: 'pending',
    current_step: 'pending',
    failure_reason: null,
    requested_at: new Date().toISOString(),
    completed_at: null,
    data: null,
  }),

  'POST /v1/transfers': (body) => {
    const req = body as { direction: string; amount: string };
    return {
      id: `tx-mock-${Date.now()}`,
      direction: req.direction,
      amount_usdc: req.amount,
      status: 'pending',
      current_step: 'pending',
      failure_reason: null,
      requested_at: new Date().toISOString(),
      completed_at: null,
      data: null,
    };
  },
  'GET /v1/positions': (_body, url) => {
    const _all = [
      {
        id: 'pos-mock-open-hl-btc',
        bot_kind: 'hyperliquid',
        strategy: 'fib',
        market: 'BTC',
        side: 'LONG',
        entry_price: '60000',
        size: '0.05',
        notional_usdc: '3000',
        leverage: '3',
        exit_price: null,
        exit_reason: null,
        realized_pnl_usdc: null,
        fees_paid_usdc: '0.40',
        status: 'open',
        opened_at: new Date(Date.now() - 600_000).toISOString(),
        closed_at: null,
        created_at: new Date(Date.now() - 605_000).toISOString(),
        tx_hash: null,
        market_id: null,
        take_profit: '61850',
        stop_loss: '59100',
      },
      {
        id: 'pos-mock-open-btc-5m',
        bot_kind: 'polymarket',
        strategy: 's1',
        market: '0xbtcconditionid000000000000000000000000000000000000000000000000aa',
        side: 'UP',
        entry_price: '0.62',
        size: '120',
        notional_usdc: '74.40',
        leverage: '1',
        exit_price: null,
        exit_reason: null,
        realized_pnl_usdc: null,
        fees_paid_usdc: '0.20',
        status: 'open',
        opened_at: new Date(Date.now() - 90_000).toISOString(),
        closed_at: null,
        created_at: new Date(Date.now() - 95_000).toISOString(),
        tx_hash: null,
        market_id: 'btc-5m',
      },
      {
        id: 'pos-mock-open-eth-5m',
        bot_kind: 'polymarket',
        strategy: 's1',
        market: '0xethconditionid000000000000000000000000000000000000000000000000bb',
        side: 'DOWN',
        entry_price: '0.48',
        size: '200',
        notional_usdc: '96.00',
        leverage: '1',
        exit_price: null,
        exit_reason: null,
        realized_pnl_usdc: null,
        fees_paid_usdc: '0.20',
        status: 'open',
        opened_at: new Date(Date.now() - 50_000).toISOString(),
        closed_at: null,
        created_at: new Date(Date.now() - 55_000).toISOString(),
        tx_hash: null,
        market_id: 'eth-5m',
      },
      {
        id: 'pos-mock-open-sol-5m',
        bot_kind: 'polymarket',
        strategy: 'fib',
        market: '0xsolconditionid000000000000000000000000000000000000000000000000cc',
        side: 'UP',
        entry_price: '0.55',
        size: '150',
        notional_usdc: '82.50',
        leverage: '1',
        exit_price: null,
        exit_reason: null,
        realized_pnl_usdc: null,
        fees_paid_usdc: '0.20',
        status: 'open',
        opened_at: new Date(Date.now() - 30_000).toISOString(),
        closed_at: null,
        created_at: new Date(Date.now() - 35_000).toISOString(),
        tx_hash: null,
        market_id: 'sol-5m',
      },
      {
        id: 'pos-mock-closed-btc-15m',
        bot_kind: 'polymarket',
        strategy: 'fib',
        market: '0xbtc15conditionid00000000000000000000000000000000000000000000dd',
        side: 'DOWN',
        entry_price: '0.58',
        size: '100',
        notional_usdc: '58.00',
        leverage: '1',
        exit_price: '1.00',
        exit_reason: 'resolved_correct',
        realized_pnl_usdc: '42.00',
        fees_paid_usdc: '0.10',
        status: 'closed',
        opened_at: new Date(Date.now() - 1800_000).toISOString(),
        closed_at: new Date(Date.now() - 900_000).toISOString(),
        created_at: new Date(Date.now() - 1805_000).toISOString(),
        tx_hash: '0xredeemmockbtc15',
        market_id: 'btc-15m',
      },
      {
        id: 'pos-mock-closed-eth-15m',
        bot_kind: 'polymarket',
        strategy: 's1',
        market: '0xeth15conditionid00000000000000000000000000000000000000000000ee',
        side: 'UP',
        entry_price: '0.51',
        size: '110',
        notional_usdc: '56.10',
        leverage: '1',
        exit_price: '0.00',
        exit_reason: 'resolved_incorrect',
        realized_pnl_usdc: '-56.10',
        fees_paid_usdc: '0.10',
        status: 'closed',
        opened_at: new Date(Date.now() - 2700_000).toISOString(),
        closed_at: new Date(Date.now() - 1800_000).toISOString(),
        created_at: new Date(Date.now() - 2705_000).toISOString(),
        tx_hash: null,
        market_id: 'eth-15m',
      },
    ];
    const s = url instanceof URL ? url.searchParams.get('status') : null;
    const items =
      s === 'open'
        ? _all.filter((p) => ['open', 'pending', 'closing'].includes(p.status))
        : s === 'closed'
          ? _all.filter((p) => ['closed', 'cancelled'].includes(p.status))
          : _all;
    return {
      items,
      total: items.length,
      total_realized_pnl: '152.34',
      total_fees_paid: '30.47',
      total_net_pnl: '121.87',
      open_count: _all.filter((p) => p.status === 'open').length,
    };
  },
  'GET /v1/pm/markets': () => ({
    items: [
      { market_id: 'btc-5m', symbol: 'BTC', timeframe_sec: 300, display_name: 'BTC 5m' },
      { market_id: 'eth-5m', symbol: 'ETH', timeframe_sec: 300, display_name: 'ETH 5m' },
      { market_id: 'sol-5m', symbol: 'SOL', timeframe_sec: 300, display_name: 'SOL 5m' },
      { market_id: 'btc-15m', symbol: 'BTC', timeframe_sec: 900, display_name: 'BTC 15m' },
      { market_id: 'eth-15m', symbol: 'ETH', timeframe_sec: 900, display_name: 'ETH 15m' },
    ],
  }),
  'GET /v1/activity': (_body, url) => buildMockActivity(url),
  'GET /v1/referrals': () => ({
    ref_code: MOCK_USER.ref_code,
    ref_link: `https://t.me/test_bot?start=${MOCK_USER.ref_code}`,
    bot_username: 'test_bot',
    l1_pct: '10.00',
    l2_pct: '2.00',
    platform_default_fee_pct: '20.00',
    l1_referrals_count: MOCK_FRIENDS.filter((f) => f.level === 1).length,
    l2_referrals_count: MOCK_FRIENDS.filter((f) => f.level === 2).length,
    total_earned_usd: MOCK_REFERRAL_EARNINGS.reduce(
      (a, e) => a + Number(e.amount_usd),
      0,
    ).toFixed(2),
    pending_earned_usd: '0.00',
  }),
  'GET /v1/referrals/members': () => ({
    items: MOCK_FRIENDS,
    total: MOCK_FRIENDS.length,
  }),
  'GET /v1/referrals/earnings': (_body, url) =>
    buildMockReferralEarnings(url),
  'GET /v1/notifications': (_body, url) => buildMockNotificationsList(url),
  'GET /v1/notifications/unread-count': () => ({
    unread_count: MOCK_NOTIFICATIONS.filter((n) => !n.read_at).length,
  }),
  'POST /v1/notifications/read-all': () => {
    let updated = 0;
    for (const n of MOCK_NOTIFICATIONS) {
      if (!n.read_at) {
        n.read_at = new Date().toISOString();
        updated++;
      }
    }
    return { ok: true, updated_count: updated };
  },
};

const MOCK_FRIENDS = [
  {
    user_id: 'friend-uuid-1',
    tg_username: 'alice_long_name',
    tg_first_name: 'Alice',
    level: 1,
    joined_at: new Date(Date.now() - 12 * 86400_000).toISOString(),
    earned_from_them_usd: '4.20',
  },
  {
    user_id: 'friend-uuid-2',
    tg_username: 'bob',
    tg_first_name: 'Bob',
    level: 1,
    joined_at: new Date(Date.now() - 30 * 86400_000).toISOString(),
    earned_from_them_usd: '1.80',
  },
  {
    user_id: 'friend-uuid-3',
    tg_username: 'invitee_of_bob',
    tg_first_name: 'Carol',
    level: 2,
    joined_at: new Date(Date.now() - 5 * 86400_000).toISOString(),
    earned_from_them_usd: '0.00',
  },
] as const;

const MOCK_REFERRAL_EARNINGS = (() => {
  const now = Date.now();
  return [
    {
      id: 'ref-mock-1',
      level: 1 as const,
      amount_usd: '1.20',
      source_fee_usd: '12.00',
      pct_applied: '10.00',
      source_kind: 'fee',
      source_ref_id: null,
      created_at: new Date(now - 1 * 86400_000).toISOString(),
      source_user_tg_username: 'alice_long_name',
      source_user_id: 'friend-uuid-1',
      paid_at: new Date(now - 1 * 86400_000 + 3600_000).toISOString(),
      paid_tx_hash: '0xref1',
    },
    {
      id: 'ref-mock-2',
      level: 1 as const,
      amount_usd: '3.00',
      source_fee_usd: '30.00',
      pct_applied: '10.00',
      source_kind: 'fee',
      source_ref_id: null,
      created_at: new Date(now - 10 * 86400_000).toISOString(),
      source_user_tg_username: 'alice_long_name',
      source_user_id: 'friend-uuid-1',
      paid_at: null,
      paid_tx_hash: null,
    },
    {
      id: 'ref-mock-3',
      level: 1 as const,
      amount_usd: '1.80',
      source_fee_usd: '18.00',
      pct_applied: '10.00',
      source_kind: 'fee',
      source_ref_id: null,
      created_at: new Date(now - 25 * 86400_000).toISOString(),
      source_user_tg_username: 'bob',
      source_user_id: 'friend-uuid-2',
      paid_at: new Date(now - 25 * 86400_000 + 3600_000).toISOString(),
      paid_tx_hash: '0xref3',
    },
  ];
})();

const MOCK_NOTIFICATIONS: Array<{
  id: string;
  kind: string;
  payload: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}> = (() => {
  const now = Date.now();
  return [
    {
      id: 'notif-mock-1',
      kind: 'fee_charged',
      payload: { amount_usd: '8.42', tx_hash: '0xfee1' },
      read_at: null,
      created_at: new Date(now - 1 * 3600_000).toISOString(),
    },
    {
      // Auto circuit-breaker pause (per-user daily-loss stop) — shows the
      // reason figures + auto-resume time in the feed.
      id: 'notif-mock-pause',
      kind: 'pm_trading_paused',
      payload: {
        reason: 'daily_loss',
        pnl_24h: '-63.00',
        limit_pct: 15,
        resume_at: new Date(now + 52 * 60_000).toISOString(),
      },
      read_at: null,
      created_at: new Date(now - 8 * 60_000).toISOString(),
    },
    {
      id: 'notif-mock-3',
      kind: 'withdrawal_decision',
      payload: { decision: 'approved', withdrawal_id: 'w-mock-1' },
      read_at: null,
      created_at: new Date(now - 12 * 3600_000).toISOString(),
    },
    {
      id: 'notif-mock-5',
      kind: 'config_changed',
      payload: { what: 'fee_pct' },
      read_at: new Date(now - 48 * 3600_000).toISOString(),
      created_at: new Date(now - 50 * 3600_000).toISOString(),
    },
  ];
})();

function buildMockNotificationsList(url: URL): unknown {
  const cursor = url.searchParams.get('cursor');
  const limit = Number(url.searchParams.get('limit') ?? 20);
  const unreadOnly = url.searchParams.get('unread_only') === 'true';
  let pool = MOCK_NOTIFICATIONS.filter((n) => !unreadOnly || !n.read_at);
  if (cursor) {
    try {
      const decoded = JSON.parse(atob(cursor));
      pool = pool.filter(
        (n) =>
          n.created_at < decoded.ts ||
          (n.created_at === decoded.ts && n.id < decoded.id),
      );
    } catch {
      /* bad cursor → empty */
    }
  }
  const page = pool.slice(0, limit);
  const next_cursor =
    pool.length > limit && page.length
      ? btoa(
          JSON.stringify({
            ts: page[page.length - 1].created_at,
            id: page[page.length - 1].id,
          }),
        )
      : null;
  return { items: page, next_cursor };
}

function buildMockReferralEarnings(url: URL): unknown {
  const level = url.searchParams.get('level');
  const sourceUserId = url.searchParams.get('source_user_id');
  const fromTs = url.searchParams.get('from_ts');
  const toTs = url.searchParams.get('to_ts');
  let pool = MOCK_REFERRAL_EARNINGS.slice();
  if (level) pool = pool.filter((e) => String(e.level) === level);
  if (sourceUserId) pool = pool.filter((e) => e.source_user_id === sourceUserId);
  if (fromTs) pool = pool.filter((e) => e.created_at >= fromTs);
  if (toTs) pool = pool.filter((e) => e.created_at < toTs);
  return {
    items: pool,
    total: pool.length,
    total_amount_usd: pool
      .reduce((a, e) => a + Number(e.amount_usd), 0)
      .toFixed(2),
  };
}

/**
 * In-memory mock activity feed. We synthesise ~50 events across all
 * kinds so the page demos cursor pagination, filters, and the
 * counts header without a backend.
 */
const MOCK_ACTIVITY = (() => {
  const now = Date.now();
  const items: Array<{
    kind: string;
    id: string;
    created_at: string;
    payload: Record<string, unknown>;
  }> = [];
  let seq = 0;
  const next = () => ({ id: `mock-${++seq}`, created_at: new Date(now - seq * 600_000).toISOString() });

  // Rotate across all mocked markets so Activity exercises the
  // per-row ``market_id`` → ``display_name`` lookup.
  const MOCK_MARKET_ROTATION = [
    'btc-5m',
    'eth-5m',
    'sol-5m',
    'btc-15m',
    'eth-15m',
  ] as const;
  for (let i = 0; i < 30; i++) {
    const { id, created_at } = next();
    const isWin = i % 3 === 0;
    const isCancel = i % 7 === 0;
    const marketId = MOCK_MARKET_ROTATION[i % MOCK_MARKET_ROTATION.length];
    items.push({
      kind: isCancel ? 'trade.cancelled' : 'trade.settled',
      id,
      created_at,
      payload: {
        position_id: id,
        bot_kind: 'polymarket',
        strategy: 's1',
        // Raw condition_id-shaped string (0x + 64 hex chars) —
        // webapp resolves display via ``market_id`` in prod, so this
        // mock string only surfaces if pm-markets cache misses. Keep
        // it valid hex so ``isRawMarketHash`` recognises it and the
        // legacy fallback collapses to the generic label rather than
        // leaking ugly text.
        market: `0x${i.toString(16).padStart(2, '0')}${'0'.repeat(62)}`,
        side: i % 2 === 0 ? 'UP' : 'DOWN',
        entry_price: '0.62',
        exit_price: isWin ? '1.00' : '0.00',
        size: '120',
        notional_usdc: '74.40',
        realized_pnl_usdc: isCancel ? null : isWin ? '45.60' : '-74.40',
        exit_reason: isWin ? 'resolved_correct' : 'resolved_incorrect',
        closed_at: created_at,
        tx_hash: isWin ? `0xredeem${i.toString().padStart(2, '0')}` : null,
        market_id: marketId,
      },
    });
  }
  // Deposits + withdrawals
  for (let i = 0; i < 6; i++) {
    const { id, created_at } = next();
    items.push({
      kind: 'deposit',
      id,
      created_at,
      payload: {
        deposit_id: id,
        chain_id: i % 2 === 0 ? 137 : 56,
        asset: i % 2 === 0 ? 'USDC' : 'USDT',
        amount_usd: '100.00',
        status: 'confirmed',
        tx_hash: `0xdeposit${i}`,
        confirmations: 12,
      },
    });
  }
  for (let i = 0; i < 4; i++) {
    const { id, created_at } = next();
    items.push({
      kind: 'withdrawal',
      id,
      created_at,
      payload: {
        withdrawal_id: id,
        chain_id: 137,
        asset: 'USDC',
        amount_usd: '50.00',
        fee_usd: '1.00',
        to_address: '0xC0FFEE0000000000000000000000000000000099',
        status: 'completed',
        tx_hash: `0xwithdraw${i}`,
        rejection_reason: null,
      },
    });
  }
  // Fees + refs
  for (let i = 0; i < 4; i++) {
    const { id, created_at } = next();
    items.push({
      kind: 'fee',
      id,
      created_at,
      payload: {
        fee_id: id,
        amount_usd: '8.42',
        fee_pct_applied: '20',
        tx_hash: `0xfee${i}`,
      },
    });
  }
  for (let i = 0; i < 3; i++) {
    const { id, created_at } = next();
    items.push({
      kind: 'ref',
      id,
      created_at,
      payload: {
        ref_id: id,
        level: ((i % 2) + 1) as 1 | 2,
        amount_usd: '1.20',
        pct_applied: '10',
        source_kind: 'fee',
        paid_at: i % 2 === 0 ? created_at : null,
        paid_tx_hash: i % 2 === 0 ? `0xref${i}` : null,
      },
    });
  }
  items.sort((a, b) =>
    a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0,
  );
  return items;
})();

const ACTIVITY_KINDS_BY_TYPE: Record<string, ReadonlySet<string>> = {
  all: new Set(MOCK_ACTIVITY.map((it) => it.kind)),
  trades: new Set(['trade.settled', 'trade.cancelled']),
  // Wallet now matches the API: everything that's not a trade.
  wallet: new Set([
    'deposit',
    'withdrawal',
    'fee',
    'ref',
  ]),
};

function buildMockActivity(url: URL): unknown {
  const type = url.searchParams.get('type') ?? 'all';
  const cursor = url.searchParams.get('cursor');
  const limit = Number(url.searchParams.get('limit') ?? 20);
  const botKindFilter = url.searchParams.get('bot_kind');
  const resultFilter = url.searchParams.get('result');
  const fromTs = url.searchParams.get('from_ts');
  const toTs = url.searchParams.get('to_ts');
  const kindsParam = url.searchParams.get('kinds');
  const allowed = ACTIVITY_KINDS_BY_TYPE[type] ?? ACTIVITY_KINDS_BY_TYPE.all;
  const explicitKinds = kindsParam
    ? new Set(kindsParam.split(',').map((s) => s.trim()).filter(Boolean))
    : null;

  const matchesFilters = (it: (typeof MOCK_ACTIVITY)[number]): boolean => {
    if (!allowed.has(it.kind)) return false;
    if (explicitKinds && !explicitKinds.has(it.kind)) return false;
    if (fromTs && it.created_at < fromTs) return false;
    if (toTs && it.created_at >= toTs) return false;
    if (botKindFilter || resultFilter) {
      // bot_kind / result apply only to trade rows.
      const isTrade =
        it.kind === 'trade.settled' || it.kind === 'trade.cancelled';
      if (botKindFilter && isTrade) {
        const k = (it.payload as { bot_kind?: string }).bot_kind;
        if (k !== botKindFilter) return false;
      }
      if (resultFilter) {
        if (resultFilter === 'cancelled') {
          if (it.kind !== 'trade.cancelled') return false;
        } else if (resultFilter === 'won') {
          if (it.kind !== 'trade.settled') return false;
          const pnl = Number(
            (it.payload as { realized_pnl_usdc: string | null }).realized_pnl_usdc ?? 0,
          );
          if (pnl <= 0) return false;
        } else if (resultFilter === 'lost') {
          if (it.kind !== 'trade.settled') return false;
          const pnl = Number(
            (it.payload as { realized_pnl_usdc: string | null }).realized_pnl_usdc ?? 0,
          );
          if (pnl >= 0) return false;
        }
      }
    }
    return true;
  };

  let pool = MOCK_ACTIVITY.filter(matchesFilters);
  if (cursor) {
    try {
      const decoded = JSON.parse(atob(cursor));
      pool = pool.filter(
        (it) =>
          it.created_at < decoded.ts ||
          (it.created_at === decoded.ts && it.id < decoded.id),
      );
    } catch {
      /* ignore — bad cursor returns empty page */
    }
  }
  const page = pool.slice(0, limit);
  const nextCursor =
    pool.length > limit && page.length
      ? btoa(
          JSON.stringify({
            ts: page[page.length - 1].created_at,
            id: page[page.length - 1].id,
          }),
        )
      : null;

  // Counts honour the same filter set so the trade-split matches what
  // the user sees when narrowing on the trades tab.
  const filtered = MOCK_ACTIVITY.filter(matchesFilters);
  const tradeWon = filtered.filter(
    (it) =>
      it.kind === 'trade.settled' &&
      Number(
        (it.payload as { realized_pnl_usdc: string | null }).realized_pnl_usdc ?? 0,
      ) > 0,
  ).length;
  const tradeLost = filtered.filter(
    (it) =>
      it.kind === 'trade.settled' &&
      Number(
        (it.payload as { realized_pnl_usdc: string | null }).realized_pnl_usdc ?? 0,
      ) < 0,
  ).length;
  const tradeCanceled = filtered.filter(
    (it) => it.kind === 'trade.cancelled',
  ).length;
  const filteredPnl = filtered
    .filter((it) => it.kind === 'trade.settled')
    .reduce(
      (acc, it) =>
        acc +
        Number(
          (it.payload as { realized_pnl_usdc: string | null }).realized_pnl_usdc ??
            0,
        ),
      0,
    );

  const isWalletTab = type === 'wallet';
  return {
    items: page,
    next_cursor: nextCursor,
    counts: {
      total: isWalletTab
        ? filtered.length
        : tradeWon + tradeLost + tradeCanceled,
      trade_won: isWalletTab ? 0 : tradeWon,
      trade_lost: isWalletTab ? 0 : tradeLost,
      trade_canceled: isWalletTab ? 0 : tradeCanceled,
    },
    filtered_pnl: isWalletTab ? '0' : filteredPnl.toFixed(2),
  };
}

function fallbackForPath(path: string): unknown {
  // Collections — empty + zeros. Singletons — empty object.
  if (
    path.includes('/history') ||
    path.includes('/list') ||
    path.endsWith('/positions') ||
    path.endsWith('/withdrawals') ||
    path.endsWith('/deposits') ||
    path.endsWith('/earnings') ||
    path.endsWith('/members')
  ) {
    return { items: [], total: 0 };
  }
  return {};
}

async function handleMockRequest(input: RequestInfo | URL, init?: RequestInit) {
  const url = new URL(
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url,
    window.location.origin,
  );
  if (!url.pathname.startsWith('/api/')) {
    return null; // pass-through for non-API requests
  }
  const apiPath = url.pathname.replace(/^\/api/, '');
  const method = (init?.method ?? 'GET').toUpperCase();
  const key = `${method} ${apiPath}`;
  let body: unknown = undefined;
  try {
    body = init?.body ? JSON.parse(String(init.body)) : undefined;
  } catch {
    body = init?.body;
  }
  // Pattern-style fixtures (id-in-path) handled before the exact-match
  // dictionary. Keep the list short — most endpoints fit the literal key.
  const markReadMatch = method === 'POST' && /^\/v1\/notifications\/[^/]+\/read$/.test(apiPath);
  const handler = markReadMatch
    ? async () => {
        const id = apiPath.split('/')[3];
        const n = MOCK_NOTIFICATIONS.find((x) => x.id === id);
        if (n && !n.read_at) n.read_at = new Date().toISOString();
        return { ok: !!n };
      }
    : FIXTURES[key];
  const payload = handler
    ? await handler(body, url)
    : fallbackForPath(apiPath);
  return new Response(JSON.stringify(payload), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  });
}

function installFetchInterceptor() {
  const original = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const mocked = await handleMockRequest(input, init);
    if (mocked) return mocked;
    return original(input, init);
  };
}

export function installMockEnvIfRequested(): boolean {
  if (!mockEnabled()) return false;
  installMockTelegramWebApp();
  installFetchInterceptor();
  // eslint-disable-next-line no-console
  console.info(
    '[polybot] mock mode active — `?mock=0` in the URL turns it off',
  );
  return true;
}

import { emitMockEvent } from './realtime';

/**
 * Mock realtime driver — emits per-market `global:price` ticks each
 * second so the multi-market hero breathes in dev without the
 * trading_engine. Wire-format mirrors the real producer (PR #311):
 * ``market_id`` + ``symbol`` + ``oracle_price`` per envelope, with
 * the legacy ``btc`` field still set on BTC envelopes.
 */
const MOCK_MARKET_PRICE_ANCHORS: ReadonlyArray<{
  market_id: string;
  symbol: string;
  anchor: number;
  ptb: number;
}> = [
  { market_id: 'btc-5m', symbol: 'BTC', anchor: 65000, ptb: 64980 },
  { market_id: 'eth-5m', symbol: 'ETH', anchor: 3400, ptb: 3398 },
  { market_id: 'sol-5m', symbol: 'SOL', anchor: 165, ptb: 164.7 },
  { market_id: 'btc-15m', symbol: 'BTC', anchor: 65000, ptb: 64950 },
  { market_id: 'eth-15m', symbol: 'ETH', anchor: 3400, ptb: 3392 },
];

export function startMockRealtimeDriver(): () => void {
  if (!mockEnabled()) return () => undefined;
  // Per-market state so markets don't move in lockstep. Walk + bid
  // sigmoid scaled to the anchor so BTC ($65k) and SOL ($165) both
  // animate visibly without one flatlining.
  const state = MOCK_MARKET_PRICE_ANCHORS.map((m) => ({
    ...m,
    price: m.anchor + (Math.random() - 0.5) * m.anchor * 0.002,
  }));
  const id = window.setInterval(() => {
    for (const m of state) {
      m.price += (Math.random() - 0.5) * m.anchor * 0.0001;
      const delta = m.price - m.ptb;
      const upBid = +(0.5 + Math.tanh(delta / (m.anchor * 0.001)) * 0.45).toFixed(3);
      const downBid = +(1 - upBid).toFixed(3);
      const payload: Record<string, string> = {
        market_id: m.market_id,
        symbol: m.symbol,
        source: 'rtds',
        oracle_price: m.price.toFixed(2),
        epoch_id: `mock-${m.market_id}-epoch`,
        ptb: m.ptb.toFixed(2),
        up_bid: upBid.toFixed(2),
        down_bid: downBid.toFixed(2),
      };
      if (m.symbol === 'BTC') {
        // Legacy back-compat field — keep emitting on BTC envelopes
        // so this mock matches the real producer (PR #311).
        payload.btc = m.price.toFixed(2);
      }
      emitMockEvent({
        channel: 'global:price',
        type: 'tick',
        ts: Date.now() / 1000,
        payload,
      });
    }
  }, 1000);
  return () => window.clearInterval(id);
}

export function isMockEnabled(): boolean {
  return mockEnabled();
}
